'use client';

import {
  linksToHcDomainCriterion,
  refreshLinkTracking,
} from '@hc-frontend/third-party-snowplow';
import type React from 'react';
import { useEffect, useState } from 'react';

import { createCompleteTrackingForSnowplowFactory } from '../../../adapters/create-complete-tracking-for-snowplow';
import type { CompleteTrackingRepository } from '../../../domain';
import { CompleteTrackingContext } from './complete-tracking-context';
import type {
  CompleteTrackingProviderProps,
  PrebuiltAdapter,
} from './complete-tracking-provider.types';

function getActualAdapter(
  adapter: CompleteTrackingProviderProps['adapter'],
): Promise<CompleteTrackingRepository | undefined> {
  // If the adapter is a prebuilt adapter, we can use the adapter artifacts and return an instance
  const asPrebuiltAdapter = adapter as PrebuiltAdapter;
  if (asPrebuiltAdapter.type === 'snowplow')
    return Promise.resolve(
      createCompleteTrackingForSnowplowFactory(asPrebuiltAdapter.props),
    );

  // If the adapter is a function, we can invoke it and return the result
  if (adapter instanceof Function) {
    const invokeResult = adapter();
    if (invokeResult instanceof Promise) return invokeResult;
    return Promise.resolve(invokeResult);
  }

  // If the adapter is already an instance, we can return it
  return Promise.resolve(adapter as CompleteTrackingRepository);
}

export function CompleteTrackingProvider({
  children,
  adapter,
}: CompleteTrackingProviderProps): React.ReactNode | null {
  const [adapterInstance, setAdapterInstance] =
    useState<CompleteTrackingRepository | null>(null);

  useEffect(() => {
    if (adapter && !adapterInstance)
      getActualAdapter(adapter).then((instance) => {
        if (instance) setAdapterInstance(instance);
      });
  }, [adapter, adapterInstance]);

  useEffect(() => {
    if (adapterInstance) {
      refreshLinkTracking({
        crossDomainLinkerCriterion: linksToHcDomainCriterion,
      });
    }
  }, [adapterInstance]);

  if (adapterInstance) {
    return (
      <CompleteTrackingContext.Provider value={adapterInstance}>
        {children}
      </CompleteTrackingContext.Provider>
    );
  }

  return null;
}
